/**
 * Contract between the Character Editor's **Animation preview** tab and the
 * 2400×1400 "Animation" Rive scene (`sb_animation.riv`) — a SINGLE artboard that
 * shows every character at once (child + dad now, mom later), each recoloured
 * independently via its own nested ViewModel.
 *
 * STRUCTURE (re-verified 2026-07-30 after a reimport, with `.temp/rive-inspect.mjs`):
 *   • 1 artboard "Animation" (2400×1400) driven by the "Story animation" SM, with
 *     30 timelines — ALL One Shot (`loopValue` 0), as the scene requires.
 *   • Root view model **ViewModelAnimation** (the artboard's data context) with
 *     two nested-VM properties: `propertyOfViewModelChild` → ViewModelChild,
 *     `propertyOfViewModelDad` → ViewModelDad.
 *   • **ViewModelChild** — 9 colours + `isGirl`/`frecklesOn`/`glassesOn`
 *     (canonical names, identical to the head editor's ViewModel1).
 *   • **ViewModelDad** — the same 12 properties, `d`-prefixed (`dSkinBase`,
 *     `dIsGirl`, …).
 *
 * The top-left chooser only switches which character's editor panel + photo you
 * EDIT — the preview always shows every character. Each character's selection is
 * written to its own nested VM (see {@link SCENE_CHARACTER_BINDINGS}), so Child
 * and Dad recolour/toggle fully independently on the one artboard.
 *
 * Only COLOUR + BOOLEAN properties are bound in the VMs, so those drive live
 * per-character. Image swaps (pupils, hairstyle PNGs, freckles/glasses images)
 * are shared file-level assets, not per-character VM slots — they stay
 * panel-only until per-character image bindings exist. Add Mom by giving
 * ViewModelAnimation a third nested-VM property and filling her binding below.
 */

/**
 * The Animation scene is its OWN .riv, separate from the head editor's file.
 *
 * v2 (2026-07-30) is the multi-scene structure: a MASTER artboard "Animation"
 * that nests one artboard per scene (`Scene_01`, `Scene_02`, …) and chains them
 * with its own state machine — verified running Scene_01 → Scene_02 at 4.017s
 * (`.temp/rive-v2-probe.mjs`). Each scene artboard is also playable on its own.
 *
 * ⚠ This is the PATCHED file, not a designer export: `sb_animation.riv` is built
 * from the newest `sb_animation-vN.riv` by `scripts/rive/import-animation.sh`,
 * which injects the per-character pose-control timelines (see
 * {@link CHARACTER_POSE_PREFIX}). Run that script after every new export —
 * pointing this at a `-vN` file directly gives a preview with dead pose sliders.
 */
export const SCENE_RIV_SRC = '/rive/sb_character_editor/sb_animation.riv';

/**
 * Candidate names for the 2400×1400 MASTER artboard (first match wins) — the one
 * that plays every scene in order. The per-scene artboards are found by
 * {@link SCENE_NAME_RE} instead.
 */
export const SCENE_ARTBOARD_CANDIDATES = ['Animation', 'Scene', 'Cartoon', 'Scene test'];

/** Pick the preferred master artboard from the file's artboard names (or undefined). */
export function pickSceneArtboard(names: string[]): string | undefined {
  const lower = names.map((n) => n.toLowerCase());
  for (const cand of SCENE_ARTBOARD_CANDIDATES) {
    const i = lower.indexOf(cand.toLowerCase());
    if (i !== -1) return names[i];
  }
  return undefined;
}

/** Reference canvas for the Scene (default SB horizontal screen). */
export const SCENE_REF_W = 2400;
export const SCENE_REF_H = 1400;

/** `scene_01`, `Scene_02`, `scene 3` … — the naming convention for a scene. */
export const SCENE_NAME_RE = /^scene[_\s-]?(\d+)$/i;

/** Scene number from a scene artboard/timeline name, or undefined. */
export function sceneNumber(name: string): number | undefined {
  const m = SCENE_NAME_RE.exec(name.trim());
  return m ? parseInt(m[1], 10) : undefined;
}

/** "Scene_01" → "Scene 1" (chip label). */
export function sceneLabel(name: string): string {
  const n = sceneNumber(name);
  return n === undefined ? name : `Scene ${n}`;
}

/**
 * The state machine that orchestrates a mounted artboard. The video controls
 * play/pause THIS. `pickSceneStateMachine` tolerates a rename and falls back to
 * whatever the artboard has — necessary here: the v2 master runs
 * "State Machine 1" while BOTH scene artboards call theirs "Scene_01" (a
 * copy-paste in Rive), so a scene's SM must be taken from its own artboard.
 */
export const SCENE_STATE_MACHINE = 'Story animation';
export const SCENE_STATE_MACHINE_CANDIDATES = [
  SCENE_STATE_MACHINE,
  'Story',
  'Animation', // the master's SM in the 2026-07-31 export (was "State Machine 1")
  'State Machine 1',
];

export function pickSceneStateMachine(names: string[]): string | undefined {
  const lower = names.map((n) => n.toLowerCase());
  for (const cand of SCENE_STATE_MACHINE_CANDIDATES) {
    const i = lower.indexOf(cand.toLowerCase());
    if (i !== -1) return names[i];
  }
  return names[0]; // fall back to whatever SM the artboard has
}

// ── Scene manifest (checkpoints) ──────────────────────────────────────────────
/**
 * One scene on the master timeline. `durationSec` and `startSec` are AUTHORED
 * values read from the file, not measured — the master artboard holds one timeline
 * per scene (`Scene_01` = 4.000s, `Scene_02` = 3.000s), which is exactly the
 * checkpoint manifest. Durations therefore stay correct as scenes are added,
 * with no learning and no guessing.
 */
export interface SceneMark {
  /** Timeline name on the master artboard, e.g. "Scene_01". */
  name: string;
  /** Scene number parsed from the name (ordering). */
  number: number;
  durationSec: number;
  /** Cumulative offset where this scene starts in the full run. */
  startSec: number;
}

/**
 * Read the scene manifest from the MOUNTED master artboard's timelines, ordered by
 * scene number. Read-only (`animationByIndex` hands back a reference and instances
 * nothing). Empty when the artboard has no `scene_NN` timelines — e.g. the old
 * single-scene file, where the control strip just falls back to one unnamed run.
 */
export function readSceneManifest(rive: unknown, artboardName?: string): SceneMark[] {
  interface LowLevelArtboardTimelines {
    animationCount(): number;
    animationByIndex(i: number): { name?: string; duration?: number; fps?: number } | null;
  }
  const r = rive as {
    artboard?: LowLevelArtboardTimelines;
    file?: { artboardByName(name: string): LowLevelArtboardTimelines | null };
  } | null;
  // Prefer the NAMED artboard (the master) via the file handle: the manifest must
  // stay readable while a single scene is mounted, otherwise picking one scene
  // would erase the checkpoints — and with them the way back to "all scenes".
  let artboard: LowLevelArtboardTimelines | null | undefined;
  try {
    artboard = artboardName ? r?.file?.artboardByName(artboardName) : undefined;
  } catch {
    artboard = undefined;
  }
  artboard ??= r?.artboard;
  if (!artboard || typeof artboard.animationCount !== 'function') return [];
  try {
    const found: Omit<SceneMark, 'startSec'>[] = [];
    for (let i = 0; i < artboard.animationCount(); i += 1) {
      const anim = artboard.animationByIndex(i);
      const name = anim?.name;
      const number = name ? sceneNumber(name) : undefined;
      if (!name || number === undefined) continue;
      const fps = anim?.fps || 60;
      const durationSec = (anim?.duration ?? 0) / fps;
      if (durationSec > 0) found.push({ name, number, durationSec });
    }
    found.sort((a, b) => a.number - b.number);
    let at = 0;
    return found.map((s) => {
      const mark = { ...s, startSec: at };
      at += s.durationSec;
      return mark;
    });
  } catch {
    return [];
  }
}

/**
 * The v2 master artboard has **no ViewModel linked** (Rive reports "Could not find
 * a View Model linked to Artboard Animation"), so `autoBind` leaves
 * `rive.viewModelInstance` null and every colour/bool write would be skipped. The
 * scene artboards each have `ViewModelAnimation` linked, so playing a single scene
 * is unaffected.
 *
 * As a stop-gap this binds a `ViewModelAnimation` instance to the mounted artboard
 * by hand. Whether the nested scene artboards actually READ that instance depends
 * on how their data contexts are wired in Rive and could not be settled headlessly
 * (the master never settles, so `advance(0)` always reports a change). The real fix
 * is Rive-side: link ViewModelAnimation to the master artboard and pass it into
 * both nested artboard instances.
 *
 * Returns the bound instance, or null if there was nothing to do / it failed.
 */
export function ensureViewModelBound(rive: unknown, viewModelName = 'ViewModelAnimation'): unknown {
  interface LowLevelVm { name?: string; defaultInstance(): unknown }
  interface LowLevelFile { viewModelCount(): number; viewModelByIndex(i: number): LowLevelVm | null }
  const r = rive as {
    viewModelInstance?: unknown;
    artboard?: { bindViewModelInstance(instance: unknown): void };
    file?: LowLevelFile;
  } | null;
  if (!r?.artboard || typeof r.artboard.bindViewModelInstance !== 'function') return null;
  try {
    if (r.viewModelInstance) return null; // autoBind already found one
    const file = r.file;
    if (!file || typeof file.viewModelCount !== 'function') return null;
    for (let i = 0; i < file.viewModelCount(); i += 1) {
      const vm = file.viewModelByIndex(i);
      if (vm?.name !== viewModelName) continue;
      const instance = vm.defaultInstance();
      if (!instance) return null;
      r.artboard.bindViewModelInstance(instance);
      return instance;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Loop audit ────────────────────────────────────────────────────────────────
/**
 * The Animation scene is a CARTOON: it must play once and stop. A looping
 * timeline is an authoring error to be fixed in Rive (loop mode → **One Shot**),
 * never worked around in the app — the editor plays the file 1:1.
 *
 * Rive's `Loop` enum: 0 = one shot, 1 = loop, 2 = ping-pong.
 *
 * EXCEPTION: the injected `CTRL …` pose timelines (see
 * {@link CHARACTER_POSE_PREFIX}) loop ON PURPOSE — they're 1-second editor
 * scaffolding that only ever gets scrubbed, never played, and the JS runtime
 * auto-stops a one-shot on the frame it's scrubbed to. They are not part of the
 * cartoon, so the audit skips them; without that, importing an export turned the
 * banner into a 30-line false alarm.
 */
export type LoopMode = 'loop' | 'ping-pong';

/** Editor scaffolding, not story content — exempt from the loop audit. */
const isPoseControlTimeline = (name: string | undefined): boolean => !!name?.startsWith('CTRL ');

export interface LoopOffender {
  name: string;
  mode: LoopMode;
  /** Artboard the timeline lives on (set by the whole-file audit). */
  artboard?: string;
}

/** Minimal shape of the low-level artboard we touch (see rive_advanced.mjs.d.ts). */
interface LowLevelArtboard {
  animationCount(): number;
  animationByIndex(i: number): { name?: string; loopValue?: number } | null;
  advance(sec: number): boolean;
}

/**
 * READ-ONLY audit of every timeline on the mounted artboard: returns the ones
 * whose Rive loop mode isn't One Shot. Nothing is instanced, advanced or
 * modified — `animationByIndex` only hands back a reference to the artboard's
 * own animation.
 *
 * Returns `null` when the audit can't run (artboard not live yet, or the runtime
 * changed shape) so the caller can say "not verified" instead of "clean". Only
 * covers THIS artboard's timelines — animations inside nested artboards aren't
 * enumerable from here.
 */
export function auditArtboardLoops(rive: unknown): LoopOffender[] | null {
  // `artboard` is private in rive.d.ts but is the documented low-level handle;
  // reading it is the only way to see loop modes (they aren't in `contents`).
  const artboard = (rive as { artboard?: LowLevelArtboard } | null)?.artboard;
  if (!artboard || typeof artboard.animationCount !== 'function') return null;
  try {
    const offenders: LoopOffender[] = [];
    const count = artboard.animationCount();
    for (let i = 0; i < count; i += 1) {
      const anim = artboard.animationByIndex(i);
      if (!anim || isPoseControlTimeline(anim.name)) continue;
      const mode = anim.loopValue;
      if (mode === 1 || mode === 2) {
        offenders.push({ name: anim.name || `animation ${i + 1}`, mode: mode === 1 ? 'loop' : 'ping-pong' });
      }
    }
    return offenders;
  } catch {
    return null; // disposed mid-audit / runtime shape changed
  }
}

/**
 * Same audit across EVERY artboard in the file, so a loop in `scene_05` is caught
 * on load instead of when you finally play that far. Reaches the file handle
 * (private, like `artboard`) and reads each artboard's timelines without
 * instancing a state machine or advancing anything.
 *
 * Falls back to `null` if the file handle isn't reachable — the caller then uses
 * {@link auditArtboardLoops} on the mounted artboard alone.
 */
export function auditFileLoops(rive: unknown): LoopOffender[] | null {
  interface LowLevelFileArtboards {
    artboardCount(): number;
    artboardByIndex(i: number): (LowLevelArtboard & { name?: string }) | null;
  }
  const file = (rive as { file?: LowLevelFileArtboards } | null)?.file;
  if (!file || typeof file.artboardCount !== 'function') return null;
  try {
    const offenders: LoopOffender[] = [];
    for (let a = 0; a < file.artboardCount(); a += 1) {
      const artboard = file.artboardByIndex(a);
      if (!artboard || typeof artboard.animationCount !== 'function') continue;
      const abName = artboard.name || `artboard ${a + 1}`;
      for (let i = 0; i < artboard.animationCount(); i += 1) {
        const anim = artboard.animationByIndex(i);
        if (isPoseControlTimeline(anim?.name)) continue;
        const mode = anim?.loopValue;
        if (mode === 1 || mode === 2) {
          offenders.push({
            name: anim?.name || `animation ${i + 1}`,
            mode: mode === 1 ? 'loop' : 'ping-pong',
            artboard: abName,
          });
        }
      }
    }
    return offenders;
  } catch {
    return null;
  }
}

// ── Data-binding flush ────────────────────────────────────────────────────────
/**
 * Apply pending data-binding writes to the artboard with **zero time
 * progression**, so a colour/toggle edited while the scene is PAUSED shows up
 * immediately without the editor touching the animation.
 *
 * `Artboard::advance(0)` re-solves the artboard's objects (which is where Rive
 * picks up data-bind changes) — it advances no timeline and no state machine: the
 * SM instance isn't touched, and 0 seconds pass. This is the whole reason it's
 * allowed under the tab's "the animation is pure" rule, where the old
 * `play()`-for-one-frame poke was not (that one really did advance the story).
 *
 * Verified headlessly with `.temp/rive-databind-probe.mjs` against
 * `sb_animation.riv`: with no pending edits `advance(0)` reports no change and is
 * a no-op; right after a colour write it reports the change once and then goes
 * quiet again. Booleans that the file routes through the state machine still only
 * land once the story is playing — that's the file's wiring, not ours to fix.
 *
 * Returns true if the artboard reported a change, false if it was a no-op or the
 * low-level handle wasn't reachable. Call `drawFrame()` afterwards to repaint.
 */
export function flushDataBinds(rive: unknown): boolean {
  // `artboard` is private in rive.d.ts but is the documented low-level handle.
  const artboard = (rive as { artboard?: LowLevelArtboard } | null)?.artboard;
  if (!artboard || typeof artboard.advance !== 'function') return false;
  try {
    return artboard.advance(0);
  } catch {
    return false; // disposed mid-flush
  }
}

// ── End-of-story / loop observation ───────────────────────────────────────────
/**
 * Watch whether the scene's state machine has **settled** (the story is over).
 *
 * The runtime knows this exactly — `StateMachineInstance.advanceAndApply()`
 * returns `false` once nothing is left to advance — but the JS wrapper discards
 * the value and never clears its own `playing` flag, so it keeps its rAF loop and
 * `advance` events running forever afterwards. Nothing observable from the public
 * API tells you the story ended (measured: `didChange()` stays true,
 * `stateChangedCount()` is 0, advance deltas keep coming).
 *
 * So we shadow `advanceAndApply` on the low-level instance with a pass-through:
 * it forwards the argument, returns the runtime's own value unchanged, and only
 * reports what it saw. Playback is bit-identical — this observes the animation, it
 * does not touch it. `delete`ing the own property restores the prototype method
 * (verified with `.temp/rive-patch-probe.mjs`, which also measured this scene
 * settling at 4.033s and never resuming).
 *
 * `onSettled` fires on EVERY frame (~60/s) — the caller must debounce state
 * updates. `settled` going back to false without anyone pressing play/restart
 * means the scene restarted itself, i.e. a loop.
 *
 * Returns an un-observe function. Re-attach after `rive.reset()`: that disposes
 * the instances, and the fresh ones are unpatched.
 */
export function observeStorySettled(rive: unknown, onSettled: (settled: boolean) => void): () => void {
  interface LowLevelSmi { advanceAndApply(sec: number): boolean }
  const sms = (rive as { animator?: { stateMachines?: Array<{ instance?: LowLevelSmi }> } } | null)
    ?.animator?.stateMachines;
  if (!Array.isArray(sms) || sms.length === 0) return () => {};
  const undo: Array<() => void> = [];
  for (const sm of sms) {
    const inst = sm?.instance;
    if (!inst || typeof inst.advanceAndApply !== 'function') continue;
    // Already observed (own property shadows the prototype) — don't double-wrap.
    if (Object.prototype.hasOwnProperty.call(inst, 'advanceAndApply')) continue;
    const original = (Object.getPrototypeOf(inst) as LowLevelSmi | null)?.advanceAndApply;
    if (typeof original !== 'function') continue;
    try {
      inst.advanceAndApply = function (sec: number) {
        const keepGoing = original.call(this, sec);
        try {
          onSettled(keepGoing === false);
        } catch {
          /* never let the observer break the frame */
        }
        return keepGoing;
      };
      undo.push(() => {
        try {
          delete (inst as Partial<LowLevelSmi>).advanceAndApply;
        } catch {
          /* disposed */
        }
      });
    } catch {
      /* frozen instance — skip, the UI just loses end detection */
    }
  }
  return () => undo.forEach((f) => f());
}

/** The characters the editor knows about (more added later). */
export type CharacterId = 'child' | 'dad' | 'mom';

export interface CharacterMeta {
  id: CharacterId;
  label: string;
  /**
   * Whether this character exists in the scene right now. Drives the chooser:
   * absent characters render greyed + disabled. Child + Dad are in `sb_animation.riv`
   * today; Mom follows once her nested VM is added.
   */
  present: boolean;
}

export const CHARACTERS: CharacterMeta[] = [
  { id: 'child', label: 'Child', present: true },
  { id: 'dad', label: 'Dad', present: true },
  // Mom arrived in the 2026-07-31 11:11 export: she is in the scene with her own
  // hair image slots ("Pulled Back" / "Ponytail"), so she is selectable and her
  // hairstyles drive. Her nested ViewModel is NOT in the file yet, so her colours
  // and toggles stay panel-only — see SCENE_CHARACTER_BINDINGS.
  { id: 'mom', label: 'Mom', present: true },
];

/**
 * Per-character binding into the scene. `rootProp` is the property name on the
 * root **ViewModelAnimation** that holds this character's nested VM instance;
 * `rive.viewModelInstance.viewModel(rootProp)` returns it at runtime. Each
 * character's own VM uses different property names (child = canonical, dad =
 * `d`-prefixed), resolved per-instance by {@link resolveVmProp} — so no need to
 * enumerate all 12 names here. `undefined` ⇒ that character isn't in the scene
 * yet and is skipped (its panel still edits in-memory).
 */
export interface SceneCharacterBinding {
  rootProp?: string;
}

export const SCENE_CHARACTER_BINDINGS: Record<CharacterId, SceneCharacterBinding> = {
  child: { rootProp: 'propertyOfViewModelChild' },
  dad: { rootProp: 'propertyOfViewModelDad' },
  // Added 2026-07-31. `ViewModelMom` began life as a Rive DUPLICATE of
  // ViewModelDad — its properties shipped as `dSkinBase2`, `dIsGirl2`, … in the
  // 11:38 export and were renamed to `mSkinBase`, `mIsGirl`, … in the 11:50 one.
  // `resolveVmProp` normalises both, which is why the rename needed no code
  // change. `reportMissingCharacterBindings` says who isn't bound, in dev.
  mom: { rootProp: 'propertyOfViewModelMom' },
};

/**
 * Dev-only: characters whose `rootProp` isn't a property of the bound root
 * ViewModel — their colours and toggles are edited in the panel but written
 * nowhere. `available` is `rive.viewModelInstance.properties` mapped to names.
 */
export function reportMissingCharacterBindings(available: string[]): string[] {
  const have = new Set(available.map((n) => n.toLowerCase()));
  const missing: string[] = [];
  for (const c of CHARACTERS) {
    const prop = SCENE_CHARACTER_BINDINGS[c.id].rootProp;
    if (c.present && prop && !have.has(prop.toLowerCase())) missing.push(`${c.id} → needs "${prop}"`);
  }
  return missing;
}

// ── Per-character IMAGE slots (hair + pupil) ──────────────────────────────────
/**
 * Rive image-asset names that belong to each character, so a hairstyle / pupil
 * pick lands on the right one.
 *
 * Rive image assets are **file-level**, not per-artboard: one asset referenced by
 * every scene, so a `setRenderImage` swap applies across all of them at once (and
 * survives `rive.reset()`, which only re-instances the artboard). That's exactly
 * the sharing we want between scenes — but it also means two characters must NOT
 * share a slot, or they'd always wear the same hair.
 *
 * Slot names come from the PNG last placed in the slot in Rive, so they read like
 * hairstyles rather than positions. Re-verified against `sb_animation-v5.riv`
 * (2026-08-04, `.temp/rive-inspect.mjs`; unchanged since the 07-31 export): the
 * child's two are the same pair the head-editor file uses for its single
 * character, and the `- Brown` pair arrived with Dad.
 *
 * Front vs back was settled by LOOKING at the embedded artwork rather than the
 * names (`.temp/rive-dump-assets.mjs` writes every image out): "Quiff Short -
 * Brown" is a face-framing shape with a hole for the face ⇒ FRONT, "Buzzed Shade
 * - Brown" is the solid round head mass ⇒ BACK. Same check for the child's pair
 * ("Bangs Curtain - Dark Red" = front, "BobLong" = back). Re-run it after a
 * re-export rather than guessing from a rename.
 *
 * `undefined` ⇒ not wired yet: the panel still edits in memory, nothing is pushed.
 * A configured name that isn't in the file logs the available names in dev
 * (see `reportMissingImageSlots`) — rename here after a re-export.
 */
export interface CharacterImageSlots {
  /** Face-framing layer (fringe/bangs). */
  hairFront?: string;
  /** Hair mass behind the head. */
  hairBack?: string;
  /** Eye pupil. */
  pupil?: string;
  /** Freckles overlay. */
  freckles?: string;
  /** Eyeglasses overlay. */
  glasses?: string;
  /**
   * Facial hair — a third LAYERED slot, composited exactly like the hair pair
   * (`assets/<Character>/beard/<Style>/<Style>_01..0N.png` recoloured with the
   * selected hair colour's own beard ramp). **Dad only** (Tamara, 2026-08-04):
   * nobody else has beard art or a beard shape in the scene, and the editor gates
   * the control on the folder, so a character without one shows no beard picker.
   */
  beard?: string;
}

export const SCENE_CHARACTER_IMAGE_SLOTS: Record<CharacterId, CharacterImageSlots> = {
  child: {
    hairFront: 'Bangs Curtain - Dark Red',
    hairBack: 'BobLong',
    pupil: 'Pupil-blue-ps',
    freckles: 'Freckles',
    glasses: 'Glasses-fix',
  },
  dad: {
    // Renamed in the 2026-08-05 15:00 export: `Quiff Short - Brown` → `Quiff-preview`,
    // `Buzzed Shade - Brown` → `Buzz-preview` (the designer replaced the PNG in each
    // slot, and Rive renames the asset with it). Until this was chased, Dad's
    // hairstyle pick — and going bald — silently went nowhere in both tabs; the dev
    // warning from `reportMissingImageSlots` is what caught it. Front vs back settled
    // by LOOKING at the dumped artwork, not the names: "Quiff-preview" is a
    // face-framing shape with a hole for the face ⇒ FRONT, "Buzz-preview" is the solid
    // round head mass ⇒ BACK. Beware `Body Preview`, which is NOT a slot we drive.
    hairFront: 'Quiff-preview',
    hairBack: 'Buzz-preview',
    // Dad's own pupil / freckles / glasses — he shared the child's single asset
    // until the designer imported a copy per character. Lowercase, as Rive
    // exported them (`resolveCharacterImageSlots` returns the file's real casing).
    pupil: 'pupil-dad',
    freckles: 'freckles-dad',
    glasses: 'glasses-dad',
    // Dad's beard (2026-08-04). Named after the PNG the designer placed in it, as
    // every slot here is — so it reads like one style ("Beard_Short") while the
    // slot actually carries whichever of `assets/Dad/beard/*` is picked.
    beard: 'Beard_Short',
  },
  mom: {
    // Front/back read off the artwork, not the names: "Pulled Back" is a crescent
    // framing the top of the face, "Ponytail" is the head mass with a tail.
    hairFront: 'Pulled Back',
    hairBack: 'Ponytail',
    pupil: 'pupil-mom',
    freckles: 'freckles-mom',
    glasses: 'glasses-mom',
  },
};

/**
 * Image assets the scene is EXPECTED to gain, per character — the names this app
 * will use the moment they exist, so a re-export needs no code change.
 *
 * Dad's pupil / freckles / glasses are the open ones: he has his own art folder
 * (`assets/Dad/{pupils,freckles,glasses}`) and his own selections, but the .riv
 * exposes a single shared asset for each, so his picks can't be pushed anywhere.
 * The fix is Rive-side: import each PNG a SECOND time under the name below and
 * bind Dad's shapes to it. `reportMissingImageSlots` prints this list in dev.
 *
 * Rive names an asset after the PNG it was imported from, so if the designer ends
 * up with a different name (`Pupil-blue-ps 1`, …), move it into
 * {@link SCENE_CHARACTER_IMAGE_SLOTS} under that real name instead — the dev
 * console logs every asset actually in the file next to this list.
 */
export const PENDING_CHARACTER_IMAGE_SLOTS: Partial<Record<CharacterId, CharacterImageSlots>> = {
  // EMPTY — every character now owns every slot. Pupils arrived first, then
  // freckles + glasses per character in the 2026-07-31 17:30 export; all of them
  // moved to the confirmed map above, which is what makes a name that goes
  // missing read as a RENAME to chase rather than as art we're still waiting for.
  //
  // Add an entry here (not above) when the designer owes a slot that doesn't
  // exist yet: `resolveCharacterImageSlots` starts driving it the moment it
  // appears in an export, with no code change, and until then the panel says the
  // pick is panel-only instead of pretending it drives.
};

/**
 * The slots to actually drive, resolved against the assets this file really has:
 * the confirmed ones plus any PENDING name that has appeared, each mapped to the
 * file's **exact** asset name.
 *
 * The case matters: the asset map is keyed by the name Rive exports, so a slot
 * configured as `Pupil-dad` against a file containing `pupil-dad` would match here
 * but then miss the map and silently never push (which is exactly what the
 * 2026-07-31 export did). Matching is case-insensitive, the returned name never is.
 *
 * A configured name the file lacks resolves to `undefined` rather than staying set,
 * so the panel's "panel only" note is accurate instead of implying a live control.
 * `reportMissingImageSlots` still reads the static map, so a rename is still
 * reported rather than quietly swallowed.
 */
export function resolveCharacterImageSlots(
  id: CharacterId,
  available: string[],
): CharacterImageSlots {
  const confirmed = SCENE_CHARACTER_IMAGE_SLOTS[id];
  // Assets not registered yet (first render): don't claim anything is missing.
  if (!available.length) return confirmed;
  const actual = new Map(available.map((n) => [n.toLowerCase(), n]));
  const out: CharacterImageSlots = {};
  for (const [slot, name] of Object.entries({
    ...confirmed,
    ...PENDING_CHARACTER_IMAGE_SLOTS[id],
  }) as [keyof CharacterImageSlots, string | undefined][]) {
    const real = name ? actual.get(name.toLowerCase()) : undefined;
    if (real) out[slot] = real;
  }
  return out;
}

/**
 * Dev-only: CONFIRMED slots that aren't in the file. Each of these is a rename to
 * chase in `SCENE_CHARACTER_IMAGE_SLOTS` — something used to drive and no longer
 * does.
 */
export function reportMissingImageSlots(available: string[]): string[] {
  const have = new Set(available.map((n) => n.toLowerCase()));
  const missing: string[] = [];
  for (const [id, slots] of Object.entries(SCENE_CHARACTER_IMAGE_SLOTS)) {
    for (const [slot, name] of Object.entries(slots)) {
      if (name && !have.has(name.toLowerCase())) missing.push(`${id}.${slot} → "${name}"`);
    }
  }
  return missing;
}

/**
 * Dev-only: PENDING slots the file still lacks — art the designer owes us, not a
 * mistake here. Kept separate from {@link reportMissingImageSlots} so a genuine
 * rename doesn't hide in a list of known gaps.
 */
export function reportPendingImageSlots(available: string[]): string[] {
  const have = new Set(available.map((n) => n.toLowerCase()));
  const pending: string[] = [];
  for (const [id, slots] of Object.entries(PENDING_CHARACTER_IMAGE_SLOTS)) {
    for (const [slot, name] of Object.entries(slots ?? {})) {
      if (name && !have.has(name.toLowerCase())) pending.push(`${id}.${slot} → needs "${name}"`);
    }
  }
  return pending;
}

/**
 * Resolve a canonical property name (e.g. `skinBase`, `isGirl`) to the ACTUAL
 * name on a character's nested VM, given that VM's property list.
 *
 * Each character's VM decorates the same 12 names differently, because they're
 * made by duplicating one another in Rive:
 *   child → `skinBase`  ·  dad → `dSkinBase`  ·  mom → `mSkinBase`
 * and the decoration MOVES between exports: Mom's set arrived as Dad's with
 * Rive's duplicate counter appended (`dSkinBase2`, 2026-07-31 11:38) and was
 * renamed to the `m` prefix hours later, with no warning. So don't encode any
 * one convention: exact match first, otherwise strip a trailing digit run and a
 * short lowercase prefix and compare. That absorbed the rename with no code
 * change, and a new character needs none either.
 *
 * Returns `undefined` if nothing matches, so an unbound property is skipped
 * rather than throwing.
 */
export function resolveVmProp(available: string[], canonical: string): string | undefined {
  const lc = canonical.toLowerCase();
  const exact = available.find((n) => n.toLowerCase() === lc);
  if (exact) return exact;
  return available.find((n) => normaliseVmProp(n) === lc);
}

/**
 * The canonical form of a VM property name: `dSkinBase2` / `dSkinBase` /
 * `skinBase` all → `skinbase`. Use it to LABEL and GROUP a character's
 * properties (the meta tables in contract.ts are keyed canonically) while
 * writing to the actual name the file uses.
 */
export const canonicalVmProp = (name: string): string => normaliseVmProp(name);

/**
 * `dSkinBase2` / `dSkinBase` / `skinBase` → `skinbase`.
 *
 * Drops Rive's duplicate counter (trailing digits) and a 1–3 character lowercase
 * prefix, but ONLY when the prefix is followed by a capital — so `frecklesOn`
 * keeps its own name instead of losing "fre".
 */
function normaliseVmProp(name: string): string {
  const noCounter = name.replace(/\d+$/, '');
  const prefixed = /^[a-z]{1,3}([A-Z].*)$/.exec(noCounter);
  return (prefixed ? prefixed[1] : noCounter).toLowerCase();
}

// ── Per-character POSE controls (head turn / mouth / smile / lids / brows) ────
/**
 * The scene rig is JOYSTICK-DRIVEN, exactly like the head editor's: one Joystick
 * per control owns its timeline and re-applies it at its own position on every
 * artboard advance, so scrubbing `D_SmileControl` from the runtime does nothing
 * (verified 2026-07-31: `.temp/rive-joystick-report.py` finds 27 joysticks on
 * `Scene_01` — 9 per character). The runtime has no joystick API.
 *
 * Same solution as the head editor, extended per character:
 * `scripts/rive/rive-add-controls.py` injects `CTRL …` timelines that key the
 * JOYSTICK POSITIONS themselves (-1 at t=0 → +1 at t=1s), which IS
 * runtime-applicable. Timeline names carry the character's own .riv prefix:
 *
 *     child → "CTRL headX"   dad → "CTRL D_headX"   mom → "CTRL M_headX"
 *
 * Import a new designer export with `scripts/rive/import-animation.sh` — the app
 * loads the PATCHED `sb_animation.riv`, the `-vN` files are the pristine sources.
 * Without the patch every pose control is simply absent from the file and the
 * Character tab's pose sliders do nothing.
 *
 * ⚠ These timelines live on the SCENE artboards, not on the master: a joystick
 * belongs to one artboard's component space, and on the master `rive.scrub` can
 * only see its own scene-chain timelines. The Character tab mounts a scene
 * artboard directly (see editorSource.ts), which is why the pose sliders drive
 * there. The Animation tab has no pose controls — poses are for editing a face,
 * and that tab is for watching the cartoon (Tamara, 2026-07-31).
 */
export const CHARACTER_POSE_PREFIX: Record<CharacterId, string> = {
  child: '',
  dad: 'D_',
  mom: 'M_',
};

/** Every injected control, in panel order. */
export const POSE_CONTROLS = [
  'headX',
  'headY',
  'mouthX',
  'mouthY',
  'smile',
  'browX',
  'browY',
  'eyesTopClose',
  'eyesBottomClose',
  'mouthOpen',
] as const;
export type PoseControl = (typeof POSE_CONTROLS)[number];

/** The CTRL timeline that drives `control` for `id`. */
export function poseTimelineName(id: CharacterId, control: PoseControl): string {
  return `CTRL ${CHARACTER_POSE_PREFIX[id]}${control}`;
}

// ── Per-character framing (zoom presets) ──────────────────────────────────────
/**
 * The region of the 2400×1400 scene each character stands in, as a pixel rect on
 * the artboard — what the preview zooms to when you pick that character.
 *
 * These are FRAMING presets, not design values: the characters walk and each
 * scene stages them differently, so a rect can only be "roughly where they are".
 * Measured off the Rive editor's Scene_01 view (Tamara's reference screenshot,
 * 2026-07-31 — the three boxes she drew round the heads) and deliberately
 * generous. Free zoom + pan sits on top of them (scroll / drag in the preview),
 * so a preset that misses is one drag away from right.
 *
 * Head-and-shoulders, not full body: the point of the zoom is editing a FACE
 * (mouth shape, smile, head turn), and a full-height rect wouldn't magnify at all
 * on a landscape canvas — the scene is already height-fitted.
 *
 * Left-to-right on the scene: child, mom, dad.
 */
export interface FrameRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const CHARACTER_FRAMES: Record<CharacterId, FrameRect> = {
  child: { x: 100, y: 20, w: 760, h: 800 },
  mom: { x: 840, y: 0, w: 740, h: 720 },
  dad: { x: 1650, y: 0, w: 730, h: 720 },
};

/** The whole scene — the default view, and what "Fit all" goes back to. */
export const SCENE_FRAME: FrameRect = { x: 0, y: 0, w: SCENE_REF_W, h: SCENE_REF_H };
